import React from 'react';
import { Users, Clock, TrendingDown, AlertCircle } from 'lucide-react';

const QueueManagement: React.FC = () => {
  const queues = [
    { id: 1, register: 'Register 1', customers: 3, avgWait: '4m', status: 'optimal' },
    { id: 2, register: 'Register 2', customers: 5, avgWait: '7m', status: 'busy' },
    { id: 3, register: 'Register 3', customers: 2, avgWait: '3m', status: 'optimal' },
    { id: 4, register: 'Register 4', customers: 7, avgWait: '10m', status: 'high' },
    { id: 5, register: 'Self-Checkout 1-4', customers: 8, avgWait: '5m', status: 'busy' }
  ];

  const hourlyWait = [
    { hour: '9 AM', avgWait: 2.1 },
    { hour: '10 AM', avgWait: 3.4 },
    { hour: '11 AM', avgWait: 4.8 },
    { hour: '12 PM', avgWait: 7.2 },
    { hour: '1 PM', avgWait: 8.5 },
    { hour: '2 PM', avgWait: 6.3 },
    { hour: '3 PM', avgWait: 5.7 }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'optimal': return 'var(--sca-teal)';
      case 'busy': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return 'var(--sca-muted)';
    }
  };

  return (
    <div className="p-6" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Queue Management
        </h1>
        <p style={{ color: 'var(--sca-muted)' }}>
          Real-time checkout monitoring and wait time optimization
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card">
          <Users size={24} className="mb-2" style={{ color: 'var(--sca-teal)' }} />
          <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            25
          </div>
          <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
            Customers in Queue
          </div>
        </div>
        <div className="card">
          <Clock size={24} className="mb-2" style={{ color: 'var(--sca-teal)' }} />
          <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            5.8m
          </div>
          <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
            Avg. Wait Time
          </div>
        </div>
        <div className="card">
          <TrendingDown size={24} className="mb-2" style={{ color: 'var(--sca-teal)' }} />
          <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            -18%
          </div>
          <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
            vs. Yesterday
          </div>
        </div>
        <div className="card">
          <AlertCircle size={24} className="mb-2" style={{ color: '#f59e0b' }} />
          <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
            1
          </div>
          <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
            High Wait Alert
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Current Queue Status
        </h3>
        <div className="space-y-3">
          {queues.map((queue) => (
            <div
              key={queue.id}
              className="p-4 rounded-lg"
              style={{ backgroundColor: 'var(--sca-hover)', border: '1px solid var(--sca-border)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
                    {queue.register}
                  </div>
                  <div className="flex gap-4 text-sm" style={{ color: 'var(--sca-muted)' }}>
                    <span>{queue.customers} customers</span>
                    <span>Avg. wait: {queue.avgWait}</span>
                  </div>
                </div>
                <div
                  className="px-3 py-1 rounded-full text-xs font-medium capitalize"
                  style={{
                    backgroundColor: `${getStatusColor(queue.status)}20`,
                    color: getStatusColor(queue.status)
                  }}
                >
                  {queue.status}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Wait Time Trends
        </h3>
        <div className="space-y-3">
          {hourlyWait.map((hour, idx) => (
            <div key={idx}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm" style={{ color: 'var(--sca-ink)' }}>{hour.hour}</span>
                <span className="text-sm font-semibold" style={{ color: 'var(--sca-teal)' }}>
                  {hour.avgWait}m avg.
                </span>
              </div>
              <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'var(--sca-border)' }}>
                <div
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: `${(hour.avgWait / 10) * 100}%`,
                    backgroundColor: hour.avgWait > 7 ? '#ef4444' : 'var(--sca-teal)'
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QueueManagement;
